import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type InvoiceRow = Database['public']['Tables']['invoices']['Row'];
export type InvoiceLineRow = Database['public']['Tables']['invoice_line_items']['Row'];
export type InvoicePaymentRow = Database['public']['Tables']['invoice_payments']['Row'];
export type InvoiceStatus = Database['public']['Enums']['invoice_status'];
export type InvoiceLineSource = Database['public']['Enums']['invoice_line_source'];
export type InvoiceAuditAction = Database['public']['Enums']['invoice_audit_action'];

export interface InvoiceListItem {
  id: string;
  clinic_id: string;
  patient_id: string;
  invoice_number: string | null;
  status: InvoiceStatus;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  vat_cents: number;
  total_cents: number;
  total_paid_cents: number;
  last_payment_at: string | null;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  voided_at: string | null;
  last_sent_at: string | null;
  last_reminder_sent_at: string | null;
  last_emailed_to: string | null;
  created_at: string;
  updated_at: string;
  patient_first_name: string | null;
  patient_last_name: string | null;
  patient_pat_number: string | null;
}

export interface InvoiceDetail extends InvoiceRow {
  lines: InvoiceLineRow[];
}

export interface InvoiceListFilters {
  status?: InvoiceStatus | 'all';
  search?: string;
}

export const invoicesListQueryKey = (clinicId: string | null, filters: InvoiceListFilters) =>
  ['invoices', 'list', clinicId, filters] as const;

export const invoiceDetailQueryKey = (invoiceId: string | null | undefined) =>
  ['invoices', 'detail', invoiceId] as const;

export function useInvoicesList(clinicId: string | null | undefined, filters: InvoiceListFilters) {
  return useQuery({
    queryKey: invoicesListQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<InvoiceListItem[]> => {
      if (!clinicId) return [];
      let q = supabase
        .from('invoices')
        .select(
          `id, clinic_id, patient_id, invoice_number, status, currency,
           subtotal_cents, discount_cents, vat_cents, total_cents,
           total_paid_cents, last_payment_at,
           issued_at, due_at, paid_at, voided_at, last_sent_at, last_reminder_sent_at, last_emailed_to,
           created_at, updated_at,
           patient:patients(first_name, last_name, pat_number)`,
        )
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (filters.status && filters.status !== 'all') {
        q = q.eq('status', filters.status);
      }

      const search = filters.search?.trim();
      if (search) {
        const pattern = `%${search}%`;
        q = q.or(`invoice_number.ilike.${pattern}`);
      }

      const { data, error } = await q;
      if (error) throw error;

      return (data ?? []).map((row): InvoiceListItem => {
        const patient = (
          row as unknown as {
            patient: { first_name: string; last_name: string; pat_number: string | null } | null;
          }
        ).patient;
        return {
          id: row.id,
          clinic_id: row.clinic_id,
          patient_id: row.patient_id,
          invoice_number: row.invoice_number,
          status: row.status,
          currency: row.currency,
          subtotal_cents: row.subtotal_cents,
          discount_cents: row.discount_cents,
          vat_cents: row.vat_cents,
          total_cents: row.total_cents,
          total_paid_cents: row.total_paid_cents ?? 0,
          last_payment_at: row.last_payment_at ?? null,
          issued_at: row.issued_at,
          due_at: row.due_at,
          paid_at: row.paid_at,
          voided_at: row.voided_at,
          last_sent_at: row.last_sent_at ?? null,
          last_reminder_sent_at: row.last_reminder_sent_at ?? null,
          last_emailed_to: row.last_emailed_to ?? null,
          created_at: row.created_at,
          updated_at: row.updated_at,
          patient_first_name: patient?.first_name ?? null,
          patient_last_name: patient?.last_name ?? null,
          patient_pat_number: patient?.pat_number ?? null,
        };
      });
    },
    staleTime: 15_000,
  });
}

export function useInvoice(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: invoiceDetailQueryKey(invoiceId ?? null),
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoiceDetail> => {
      if (!invoiceId) throw new Error('No invoice id');
      const [invoice, lines] = await Promise.all([
        supabase.from('invoices').select('*').eq('id', invoiceId).single(),
        supabase
          .from('invoice_line_items')
          .select('*')
          .eq('invoice_id', invoiceId)
          .order('position', { ascending: true }),
      ]);
      if (invoice.error) throw invoice.error;
      if (lines.error) throw lines.error;
      return {
        ...(invoice.data as InvoiceRow),
        lines: (lines.data ?? []) as InvoiceLineRow[],
      };
    },
    staleTime: 5_000,
  });
}

export interface CreateInvoiceDraftInput {
  clinic_id: string;
  patient_id: string;
  appointment_id?: string;
  due_at?: string;
  notes?: string;
}

export function useCreateInvoiceDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateInvoiceDraftInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('create_invoice_draft', {
        p_clinic_id: input.clinic_id,
        p_patient_id: input.patient_id,
        p_appointment_id: input.appointment_id,
        p_due_at: input.due_at,
        p_notes: input.notes,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: ['invoices', 'patient', row.patient_id] });
    },
  });
}

export interface UpdateInvoiceDraftInput {
  invoice_id: string;
  due_at?: string;
  notes?: string;
}

export function useUpdateInvoiceDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateInvoiceDraftInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('update_invoice_draft', {
        p_invoice_id: input.invoice_id,
        p_due_at: input.due_at,
        p_notes: input.notes,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
    },
  });
}

export interface AddInvoiceLineInput {
  invoice_id: string;
  description: string;
  quantity: number;
  unit_price_cents: number;
  vat_rate_percent?: number;
  discount_pct?: number;
  discount_amount_cents?: number;
  source?: InvoiceLineSource;
  source_id?: string;
}

export function useAddInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddInvoiceLineInput): Promise<InvoiceLineRow> => {
      const { data, error } = await supabase.rpc('add_invoice_line', {
        p_invoice_id: input.invoice_id,
        p_description: input.description,
        p_quantity: input.quantity,
        p_unit_price_cents: input.unit_price_cents,
        p_vat_rate_percent: input.vat_rate_percent,
        p_discount_pct: input.discount_pct,
        p_discount_amount_cents: input.discount_amount_cents,
        p_source: input.source,
        p_source_id: input.source_id,
      });
      if (error) throw error;
      return data as unknown as InvoiceLineRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'uninvoiced-appointments'] });
      qc.invalidateQueries({ queryKey: ['appointments', 'patient'] });
    },
  });
}

export interface UpdateInvoiceLineInput {
  line_id: string;
  invoice_id: string;
  description?: string;
  quantity?: number;
  unit_price_cents?: number;
  vat_rate_percent?: number;
  discount_pct?: number;
  discount_amount_cents?: number;
  clear_discount?: boolean;
}

export function useUpdateInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateInvoiceLineInput): Promise<InvoiceLineRow> => {
      const { data, error } = await supabase.rpc('update_invoice_line', {
        p_line_id: input.line_id,
        p_description: input.description,
        p_quantity: input.quantity,
        p_unit_price_cents: input.unit_price_cents,
        p_vat_rate_percent: input.vat_rate_percent,
        p_discount_pct: input.discount_pct,
        p_discount_amount_cents: input.discount_amount_cents,
        p_clear_discount: input.clear_discount,
      });
      if (error) throw error;
      return data as unknown as InvoiceLineRow;
    },
    onSuccess: (_row, vars) => {
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
    },
  });
}

export interface RemoveInvoiceLineInput {
  line_id: string;
  invoice_id: string;
}

export function useRemoveInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RemoveInvoiceLineInput): Promise<boolean> => {
      const { data, error } = await supabase.rpc('remove_invoice_line', {
        p_line_id: input.line_id,
      });
      if (error) throw error;
      return Boolean(data);
    },
    onSuccess: (_ok, vars) => {
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'uninvoiced-appointments'] });
      qc.invalidateQueries({ queryKey: ['appointments', 'patient'] });
    },
  });
}

export function useIssueInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('issue_invoice', {
        p_invoice_id: invoiceId,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
    },
  });
}

export interface MarkInvoicePaidInput {
  invoice_id: string;
  paid_at?: string;
  payment_method?: string;
  payment_reference?: string;
}

export function useMarkInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MarkInvoicePaidInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('mark_invoice_paid', {
        p_invoice_id: input.invoice_id,
        p_paid_at: input.paid_at,
        p_payment_method: input.payment_method,
        p_payment_reference: input.payment_reference,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
      // Auto-advance linked appointment visit status to payment_completed
      void (async () => {
        try {
          const { data: lines } = await supabase
            .from('invoice_line_items')
            .select('source_id')
            .eq('invoice_id', row.id)
            .eq('source', 'appointment');
          for (const line of lines ?? []) {
            if (!line.source_id) continue;
            try {
              await supabase.rpc('set_appointment_visit_status', {
                p_appointment_id: line.source_id as string,
                p_visit_status: 'payment_completed',
              });
              qc.invalidateQueries({ queryKey: ['appointments'] });
            } catch {
              // Non-critical
            }
          }
        } catch {
          // Non-critical
        }
      })();
    },
  });
}

export interface RevertInvoiceInput {
  invoice_id: string;
  reason: string;
}

export function useRevertInvoiceToIssued() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RevertInvoiceInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('revert_invoice_to_issued', {
        p_invoice_id: input.invoice_id,
        p_reason: input.reason,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
    },
  });
}

export interface VoidInvoiceInput {
  invoice_id: string;
  reason: string;
}

export function useVoidInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: VoidInvoiceInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('void_invoice', {
        p_invoice_id: input.invoice_id,
        p_reason: input.reason,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
    },
  });
}

export function formatMoney(cents: number, currency: string = 'DKK'): string {
  return (cents / 100).toLocaleString('da-DK', {
    style: 'currency',
    currency,
  });
}

// ── MOD-7b: Invoice emails (PDF + email send) ─────────────────────────────────

export interface InvoiceEmailRow {
  id: string;
  clinic_id: string;
  invoice_id: string;
  sent_to: string;
  sent_by: string | null;
  postmark_message_id: string | null;
  status: 'sent' | 'failed';
  error_message: string | null;
  pdf_storage_path: string;
  subject: string;
  created_at: string;
}

export const invoiceEmailsQueryKey = (invoiceId: string | null | undefined) =>
  ['invoices', 'emails', invoiceId] as const;

export function useInvoiceEmails(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: invoiceEmailsQueryKey(invoiceId),
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoiceEmailRow[]> => {
      if (!invoiceId) return [];
      const { data, error } = await supabase
        .from('invoice_emails')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as InvoiceEmailRow[];
    },
    staleTime: 10_000,
  });
}

export interface RenderInvoicePdfOutput {
  storage_path: string;
  signed_url: string;
}

export function useRenderInvoicePdf() {
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<RenderInvoicePdfOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: RenderInvoicePdfOutput;
      }>('render-invoice-pdf', { body: { invoice_id: invoiceId } });
      if (error) throw error;
      if (!data?.ok || !data.data) throw new Error('PDF render failed');
      return data.data;
    },
  });
}

export interface SendInvoiceEmailInput {
  invoice_id: string;
  recipient?: string;
  subject?: string;
  message?: string;
}

export interface SendInvoiceEmailOutput {
  invoice_email_id: string;
  postmark_message_id: string | null;
}

export function useSendInvoiceEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendInvoiceEmailInput): Promise<SendInvoiceEmailOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: SendInvoiceEmailOutput;
      }>('send-invoice-email', { body: input });
      if (error) throw error;
      if (!data?.ok || !data.data) throw new Error('Send email failed');
      return data.data;
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: invoiceEmailsQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: ['email-history'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      qc.invalidateQueries({ queryKey: ['invoices', 'patient'] });
    },
  });
}

// ── MOD-7c: Invoice payment tracking ─────────────────────────────────────────

export const invoicePaymentsQueryKey = (invoiceId: string | null | undefined) =>
  ['invoices', 'payments', invoiceId] as const;

export function useInvoicePayments(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: invoicePaymentsQueryKey(invoiceId),
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoicePaymentRow[]> => {
      if (!invoiceId) return [];
      const { data, error } = await supabase
        .from('invoice_payments')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('payment_date', { ascending: false });
      if (error) throw error;
      return (data ?? []) as InvoicePaymentRow[];
    },
    staleTime: 10_000,
  });
}

export interface RecordPaymentInput {
  invoice_id: string;
  amount_cents: number;
  payment_date?: string;
  payment_method?: string;
  payment_reference?: string;
  notes?: string;
}

export function useRecordPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RecordPaymentInput): Promise<InvoicePaymentRow> => {
      const { data, error } = await supabase.rpc('record_invoice_payment', {
        p_invoice_id: input.invoice_id,
        p_amount_cents: input.amount_cents,
        p_payment_date: input.payment_date,
        p_payment_method: input.payment_method,
        p_payment_reference: input.payment_reference,
        p_notes: input.notes,
      });
      if (error) throw error;
      return data as unknown as InvoicePaymentRow;
    },
    onSuccess: (payment) => {
      qc.invalidateQueries({ queryKey: invoicePaymentsQueryKey(payment.invoice_id) });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(payment.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
    },
  });
}

export interface CancelInvoiceInput {
  invoice_id: string;
  reason: string;
}

export function useCancelInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CancelInvoiceInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('cancel_invoice', {
        p_invoice_id: input.invoice_id,
        p_reason: input.reason,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
    },
  });
}

export interface RefundInvoiceInput {
  invoice_id: string;
  reason: string;
  refund_amount_cents?: number;
}

export function useRefundInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RefundInvoiceInput): Promise<InvoiceRow> => {
      const { data, error } = await supabase.rpc('refund_invoice', {
        p_invoice_id: input.invoice_id,
        p_reason: input.reason,
        p_refund_amount_cents: input.refund_amount_cents,
      });
      if (error) throw error;
      return data as unknown as InvoiceRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list', row.clinic_id] });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: invoicePaymentsQueryKey(row.id) });
    },
  });
}

// ── MOD-7d: Invoice reminder system ─────────────────────────────────────────

export interface InvoiceReminderRow {
  id: string;
  clinic_id: string;
  invoice_id: string;
  reminder_number: number;
  sent_to: string;
  sent_by: string | null;
  postmark_message_id: string | null;
  status: 'sent' | 'failed' | 'skipped';
  error_message: string | null;
  subject: string;
  days_overdue: number;
  is_manual: boolean;
  created_at: string;
}

export const invoiceRemindersQueryKey = (invoiceId: string | null | undefined) =>
  ['invoices', 'reminders', invoiceId] as const;

export function useInvoiceReminders(invoiceId: string | null | undefined) {
  return useQuery({
    queryKey: invoiceRemindersQueryKey(invoiceId),
    enabled: Boolean(invoiceId),
    queryFn: async (): Promise<InvoiceReminderRow[]> => {
      if (!invoiceId) return [];
      const { data, error } = await supabase
        .from('invoice_reminders')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as InvoiceReminderRow[];
    },
    staleTime: 10_000,
  });
}

export interface SendInvoiceReminderInput {
  invoice_id: string;
  custom_message?: string;
}

export interface SendInvoiceReminderOutput {
  processed_clinics: number;
  overdue_invoices: number;
  reminders_sent: number;
  reminders_skipped: number;
  errors: number;
}

export function useSendInvoiceReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SendInvoiceReminderInput): Promise<SendInvoiceReminderOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: SendInvoiceReminderOutput;
      }>('send-invoice-reminders', { body: input });
      if (error) throw error;
      if (!data?.ok || !data.data) throw new Error('Send reminder failed');
      return data.data;
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: invoiceRemindersQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(vars.invoice_id) });
    },
  });
}
